import React, { useEffect, useMemo, useState } from "react";
import { dms, inr } from "./api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader } from "./OwnerPages";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { MessageCircle, Printer, X, Trash2, Check, Ban } from "lucide-react";
import { toast } from "sonner";

// ============================================================================
// HR Phase B: leave, holidays, missed punch, advances, bonus/deduction,
// incentive, performance scorecard and payslips.
// ============================================================================

const ROLE_LABEL = { regional_manager: "Regional Manager", team_leader: "Team Leader", salesperson: "Salesperson" };
const todayIst = () => new Date(Date.now() + 5.5 * 3600 * 1000).toISOString().slice(0, 10);
const errMsg = (e) => e?.response?.data?.detail || "Something went wrong";
const fmtD = (s) => {
  if (!s) return "-";
  try { return new Date(String(s).slice(0, 10) + "T00:00:00").toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" }); }
  catch { return s; }
};
const monthName = (m) => {
  try { return new Date(m + "-01T00:00:00").toLocaleDateString("en-IN", { month: "long", year: "numeric" }); }
  catch { return m; }
};
const STATUS_TONE = { pending: "bg-amber-100 text-amber-800", approved: "bg-emerald-100 text-emerald-800",
  rejected: "bg-rose-100 text-rose-700", cancelled: "bg-slate-100 text-slate-600", active: "bg-blue-100 text-blue-800",
  closed: "bg-slate-100 text-slate-600" };
const Pill = ({ s }) => <span className={`px-2 py-0.5 rounded-full text-[11px] font-semibold capitalize ${STATUS_TONE[s] || "bg-slate-100"}`}>{s}</span>;
const GRADE_TONE = { A: "bg-emerald-600", B: "bg-blue-600", C: "bg-amber-500", D: "bg-rose-600" };
const Grade = ({ g }) => g ? <span className={`inline-flex w-6 h-6 items-center justify-center rounded text-white text-xs font-bold ${GRADE_TONE[g]}`}>{g}</span> : <span className="text-slate-400">-</span>;
const selCls = "flex h-9 rounded-md border border-input bg-background px-3 py-1 text-sm";

function Modal({ title, onClose, children, wide }) {
  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-start justify-center p-4 overflow-y-auto" onClick={onClose}>
      <div className={`bg-white dark:bg-slate-900 rounded-xl w-full ${wide ? "max-w-2xl" : "max-w-md"} mt-6 p-4`} onClick={e => e.stopPropagation()}>
        <div className="flex items-start justify-between gap-3 mb-3">
          <div className="text-lg font-bold">{title}</div>
          <button type="button" onClick={onClose} className="text-slate-500"><X size={20} /></button>
        </div>
        {children}
      </div>
    </div>
  );
}

function useStaff() {
  const [staff, setStaff] = useState([]);
  useEffect(() => { dms.hrStaff().then(r => setStaff(r.data || [])).catch(() => setStaff([])); }, []);
  return staff;
}

function StaffSelect({ staff, value, onChange }) {
  return (
    <select value={value} onChange={e => onChange(e.target.value)} className={`${selCls} w-full`}>
      <option value="">Choose staff…</option>
      {staff.map(s => <option key={s.id} value={s.id}>{s.name} ({ROLE_LABEL[s.role] || s.role})</option>)}
    </select>
  );
}

// ---------------------------------------------------------------------------
// Payslip (shared by HR payroll and staff self-service)
// ---------------------------------------------------------------------------
export function payslipText(p) {
  const r = p.row;
  const L = [`🧾 *Payslip – ${monthName(p.month)}*`, `${p.company?.name || ""}`, "", `👤 ${r.name} (${ROLE_LABEL[r.role] || r.role})`, ""];
  L.push(`Monthly salary: ${inr(r.monthly_salary)}`);
  L.push(`Payable days: ${r.payable_days} / ${p.divisor}`);
  L.push(`Present ${r.full_days} full + ${r.half_days} half | Absent ${r.absent_days}${r.leave_paid_days ? ` | Paid leave ${r.leave_paid_days}` : ""}${r.holidays ? ` | Holidays ${r.holidays}` : ""}`);
  L.push("");
  L.push("*Earnings*");
  L.push(`• Salary earned: ${inr(r.earned)}`);
  if (r.km_allowance) L.push(`• Km allowance (${r.km} km): ${inr(r.km_allowance)}`);
  if (r.incentive) L.push(`• Incentive: ${inr(r.incentive)}`);
  if (r.bonus) L.push(`• Bonus: ${inr(r.bonus)}`);
  if (r.deduction || r.advance_deduction) {
    L.push("");
    L.push("*Deductions*");
    if (r.deduction) L.push(`• Deductions: ${inr(r.deduction)}`);
    if (r.advance_deduction) L.push(`• Advance EMI: ${inr(r.advance_deduction)}`);
  }
  L.push("");
  L.push(`💰 *Net pay: ${inr(r.net)}*`);
  if (!p.final) L.push("_(Draft — month not finalized)_");
  return L.join("\n");
}

export function PayslipDialog({ month, userId, self, onClose }) {
  const [p, setP] = useState(null);
  useEffect(() => {
    (self ? dms.selfPayslip(month) : dms.hrPayslip(month, userId))
      .then(setP).catch(e => { toast.error(errMsg(e)); onClose(); });
  }, [month, userId, self]); // eslint-disable-line react-hooks/exhaustive-deps
  const print = () => {
    const el = document.getElementById("payslip-print");
    if (!el) return;
    const w = window.open("", "_blank");
    if (!w) return toast.error("Allow pop-ups to print");
    w.document.write(`<html><head><title>Payslip ${p.row.name} ${p.month}</title><style>
      body{font-family:Arial,sans-serif;padding:24px;color:#111} table{width:100%;border-collapse:collapse;margin:8px 0}
      td,th{border:1px solid #ccc;padding:6px 8px;font-size:13px;text-align:left} th{background:#f3f4f6}
      .r{text-align:right} h2,h3{margin:4px 0}</style></head><body>${el.innerHTML}</body></html>`);
    w.document.close(); w.focus(); w.print();
  };
  const share = () => window.open(`https://wa.me/?text=${encodeURIComponent(payslipText(p))}`, "_blank", "noopener");
  if (!p) return <Modal title="Payslip" onClose={onClose}><div className="text-sm text-slate-500">Loading…</div></Modal>;
  const r = p.row;
  const earn = [["Salary earned", r.earned], ["Km allowance", r.km_allowance], ["Incentive", r.incentive], ["Bonus", r.bonus]].filter(x => x[1]);
  const ded = [["Deductions", r.deduction], ["Advance EMI", r.advance_deduction]].filter(x => x[1]);
  return (
    <Modal title="Payslip" onClose={onClose} wide>
      <div className="flex gap-2 mb-3">
        <Button onClick={share} className="bg-emerald-600 hover:bg-emerald-700"><MessageCircle size={16} className="mr-1.5" />WhatsApp</Button>
        <Button variant="outline" onClick={print}><Printer size={16} className="mr-1.5" />Print / PDF</Button>
      </div>
      {!p.final && <div className="text-xs text-amber-700 mb-2">Draft — this month is not finalized yet.</div>}
      <div id="payslip-print" className="text-sm">
        <h2 style={{ fontSize: 18, fontWeight: 700 }}>{p.company?.name}</h2>
        {p.company?.address && <div className="text-xs text-slate-500">{p.company.address}</div>}
        <h3 style={{ marginTop: 8, fontWeight: 600 }}>Payslip for {monthName(p.month)}</h3>
        <table className="w-full my-2 text-sm border"><tbody>
          <tr><td className="border px-2 py-1">Name</td><td className="border px-2 py-1"><b>{r.name}</b></td>
              <td className="border px-2 py-1">Role</td><td className="border px-2 py-1">{ROLE_LABEL[r.role] || r.role}</td></tr>
          <tr><td className="border px-2 py-1">Monthly salary</td><td className="border px-2 py-1">{inr(r.monthly_salary)}</td>
              <td className="border px-2 py-1">Per day</td><td className="border px-2 py-1">{inr(r.per_day)}</td></tr>
          <tr><td className="border px-2 py-1">Payable days</td><td className="border px-2 py-1">{r.payable_days} / {p.divisor}</td>
              <td className="border px-2 py-1">Present</td><td className="border px-2 py-1">{r.full_days} full + {r.half_days} half</td></tr>
          <tr><td className="border px-2 py-1">Absent</td><td className="border px-2 py-1">{r.absent_days}{r.lwp_days ? ` + ${r.lwp_days} LWP` : ""}</td>
              <td className="border px-2 py-1">Paid leave / Holidays</td><td className="border px-2 py-1">{r.leave_paid_days || 0} / {r.holidays || 0}</td></tr>
          <tr><td className="border px-2 py-1">Late marks</td><td className="border px-2 py-1">{r.late_days}{r.late_deduct_days ? ` (-${r.late_deduct_days} day)` : ""}</td>
              <td className="border px-2 py-1">Weekly offs</td><td className="border px-2 py-1">{r.weekly_offs}</td></tr>
        </tbody></table>
        <table className="w-full my-2 text-sm border">
          <thead><tr><th className="border px-2 py-1 text-left bg-slate-50">Earnings</th><th className="border px-2 py-1 text-right bg-slate-50">Amount</th>
            <th className="border px-2 py-1 text-left bg-slate-50">Deductions</th><th className="border px-2 py-1 text-right bg-slate-50">Amount</th></tr></thead>
          <tbody>
            {Array.from({ length: Math.max(earn.length, ded.length, 1) }).map((_, i) => (
              <tr key={i}>
                <td className="border px-2 py-1">{earn[i]?.[0] || ""}</td><td className="border px-2 py-1 text-right r">{earn[i] ? inr(earn[i][1]) : ""}</td>
                <td className="border px-2 py-1">{ded[i]?.[0] || ""}</td><td className="border px-2 py-1 text-right r">{ded[i] ? inr(ded[i][1]) : ""}</td>
              </tr>
            ))}
            <tr><td className="border px-2 py-1 font-semibold">Gross</td><td className="border px-2 py-1 text-right r font-semibold">{inr(r.gross ?? r.net)}</td>
              <td className="border px-2 py-1 font-semibold">Total deductions</td><td className="border px-2 py-1 text-right r font-semibold">{inr((r.deduction || 0) + (r.advance_deduction || 0))}</td></tr>
          </tbody>
        </table>
        <div style={{ fontSize: 16, marginTop: 8 }}>Net pay: <b>{inr(r.net)}</b></div>
        {(r.adjust_items || []).length > 0 && (
          <div className="text-xs text-slate-500 mt-2">{r.adjust_items.map((a, i) => <div key={i}>{a.kind === "bonus" ? "+" : "-"} {inr(a.amount)} — {a.note}</div>)}</div>
        )}
        {r.incentive_info && <div className="text-xs text-slate-500 mt-1">Incentive: achieved {r.incentive_info.pct ?? "-"}% of target</div>}
      </div>
    </Modal>
  );
}

// ---------------------------------------------------------------------------
// Staff self-service: My Leave & Payslip
// ---------------------------------------------------------------------------
export function MyHrPage() {
  const [d, setD] = useState(null);
  const [tick, setTick] = useState(0);
  const [form, setForm] = useState(null);
  const [slip, setSlip] = useState(null);
  useEffect(() => { dms.selfHrSummary().then(setD).catch(e => toast.error(errMsg(e))); }, [tick]);
  const reload = () => setTick(t => t + 1);
  if (!d) return <div><PageHeader title="Leave & Payslip" back="/dms" /><div className="text-sm text-slate-500">Loading…</div></div>;
  const cancel = async (id) => {
    try { await dms.selfCancelLeave(id); toast.success("Leave cancelled"); reload(); } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div className="pb-24">
      <PageHeader title="Leave & Payslip" subtitle="Apply leave, fix a missed punch and see your payslips" back="/dms" />
      <div className="flex flex-wrap gap-2 mb-4">
        <Button onClick={() => setForm("leave")}>Apply Leave</Button>
        <Button variant="outline" onClick={() => setForm("reg")}>Missed Punch</Button>
        <Button variant="outline" onClick={() => dms.policyPdf(d.year).catch(e => toast.error(errMsg(e)))}>Leave & holiday policy (PDF)</Button>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
        {Object.entries(d.balance).map(([k, b]) => (
          <Card key={k} className="p-3">
            <div className="text-[11px] uppercase text-slate-500">{b.label}</div>
            <div className="text-lg font-bold">{b.left != null ? `${b.left} left` : `${b.used} taken`}</div>
            <div className="text-xs text-slate-500">{b.allowed != null ? `${b.used} used of ${b.allowed}` : "Unpaid"}{b.pending ? ` · ${b.pending} pending` : ""}</div>
          </Card>
        ))}
      </div>

      <div className="grid md:grid-cols-2 gap-4">
        <Card className="p-4">
          <div className="font-semibold mb-2">My Leaves</div>
          {d.leaves.length === 0 ? <div className="text-sm text-slate-500">No leave applied</div> : d.leaves.map(l => (
            <div key={l.id} className="flex items-start justify-between gap-2 py-1.5 border-b last:border-0 text-sm">
              <div>
                <div><b>{l.type}</b> · {fmtD(l.from)}{l.to !== l.from ? ` – ${fmtD(l.to)}` : ""} · {l.half_day ? "Half day" : `${l.units} day${l.units === 1 ? "" : "s"}`}</div>
                <div className="text-xs text-slate-500">{l.reason}{l.decision_note ? ` · HR: ${l.decision_note}` : ""}</div>
              </div>
              <div className="flex items-center gap-2 shrink-0"><Pill s={l.status} />
                {l.status === "pending" && <button className="text-xs text-rose-600" onClick={() => cancel(l.id)}>Cancel</button>}</div>
            </div>
          ))}
        </Card>
        <Card className="p-4">
          <div className="font-semibold mb-2">Missed Punch Requests</div>
          {d.regularizations.length === 0 ? <div className="text-sm text-slate-500">No requests</div> : d.regularizations.map(r => (
            <div key={r.id} className="flex items-start justify-between gap-2 py-1.5 border-b last:border-0 text-sm">
              <div><div>{fmtD(r.date)} · {r.in_time} – {r.out_time}</div><div className="text-xs text-slate-500">{r.reason}</div></div>
              <Pill s={r.status} />
            </div>
          ))}
        </Card>
        <Card className="p-4">
          <div className="font-semibold mb-2">Upcoming Holidays</div>
          {d.holidays.length === 0 ? <div className="text-sm text-slate-500">No holidays added</div> : d.holidays.map(h => (
            <div key={h.id} className="flex justify-between text-sm py-0.5"><span>{h.name}</span><span className="text-slate-500">{fmtD(h.date)}</span></div>
          ))}
        </Card>
        <Card className="p-4">
          <div className="font-semibold mb-2">Payslips</div>
          {d.payslip_months.length === 0 ? <div className="text-sm text-slate-500">Payslip comes after HR finalizes the month</div> : (
            <div className="flex flex-wrap gap-2">{d.payslip_months.map(m => (
              <Button key={m} size="sm" variant="outline" onClick={() => setSlip(m)}>{monthName(m)}</Button>))}</div>
          )}
          {d.advances.length > 0 && <>
            <div className="font-semibold mt-4 mb-1">My Advances</div>
            {d.advances.map(a => (
              <div key={a.id} className="text-sm py-0.5 flex justify-between gap-2">
                <span>{inr(a.amount)} on {fmtD(a.given_on)} · {inr(a.emi)}/month</span>
                <span className="text-slate-500">{inr(a.left)} left</span>
              </div>
            ))}
          </>}
        </Card>
      </div>
      {form === "leave" && <LeaveForm types={d.types} onClose={() => setForm(null)} onDone={() => { setForm(null); reload(); }} />}
      {form === "reg" && <RegForm onClose={() => setForm(null)} onDone={() => { setForm(null); reload(); }} />}
      {slip && <PayslipDialog month={slip} self onClose={() => setSlip(null)} />}
    </div>
  );
}

function LeaveForm({ types, onClose, onDone }) {
  const t = todayIst();
  const [f, setF] = useState({ type: "CL", from: t, to: t, half_day: false, reason: "" });
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    setBusy(true);
    try { await dms.selfApplyLeave({ ...f, to: f.half_day ? f.from : f.to }); toast.success("Leave sent to HR"); onDone(); }
    catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  return (
    <Modal title="Apply Leave" onClose={onClose}>
      <div className="space-y-3">
        <div><Label className="text-xs">Type</Label>
          <select value={f.type} onChange={e => setF({ ...f, type: e.target.value })} className={`${selCls} w-full mt-1`}>
            {Object.entries(types).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
          </select></div>
        <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={f.half_day} onChange={e => setF({ ...f, half_day: e.target.checked })} /> Half day</label>
        <div className="grid grid-cols-2 gap-2">
          <div><Label className="text-xs">From</Label><Input type="date" value={f.from} onChange={e => setF({ ...f, from: e.target.value })} className="mt-1" /></div>
          {!f.half_day && <div><Label className="text-xs">To</Label><Input type="date" value={f.to} min={f.from} onChange={e => setF({ ...f, to: e.target.value })} className="mt-1" /></div>}
        </div>
        <div><Label className="text-xs">Reason</Label><Input value={f.reason} onChange={e => setF({ ...f, reason: e.target.value })} className="mt-1" /></div>
        <div className="text-xs text-slate-500">Sundays and company holidays are not counted.</div>
        <Button onClick={submit} disabled={busy} className="w-full">Send to HR</Button>
      </div>
    </Modal>
  );
}

function RegForm({ onClose, onDone }) {
  const y = new Date(Date.now() + 5.5 * 3600 * 1000 - 86400000).toISOString().slice(0, 10);
  const [f, setF] = useState({ date: y, in_time: "10:00", out_time: "19:00", reason: "" });
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    setBusy(true);
    try { await dms.selfRegularize(f); toast.success("Request sent to HR"); onDone(); }
    catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  return (
    <Modal title="Missed Punch" onClose={onClose}>
      <div className="space-y-3">
        <div className="text-xs text-slate-500">Forgot to punch in or out? Tell HR the real times. Once approved, the day counts in attendance and salary.</div>
        <div><Label className="text-xs">Date</Label><Input type="date" value={f.date} max={y} onChange={e => setF({ ...f, date: e.target.value })} className="mt-1" /></div>
        <div className="grid grid-cols-2 gap-2">
          <div><Label className="text-xs">In time</Label><Input type="time" value={f.in_time} onChange={e => setF({ ...f, in_time: e.target.value })} className="mt-1" /></div>
          <div><Label className="text-xs">Out time</Label><Input type="time" value={f.out_time} onChange={e => setF({ ...f, out_time: e.target.value })} className="mt-1" /></div>
        </div>
        <div><Label className="text-xs">Reason</Label><Input value={f.reason} onChange={e => setF({ ...f, reason: e.target.value })} className="mt-1" /></div>
        <Button onClick={submit} disabled={busy} className="w-full">Send to HR</Button>
      </div>
    </Modal>
  );
}

// ---------------------------------------------------------------------------
// HR: Leave & Requests (approvals)
// ---------------------------------------------------------------------------
export function HrRequestsPage() {
  const [status, setStatus] = useState("pending");
  const [d, setD] = useState(null);
  const [tick, setTick] = useState(0);
  useEffect(() => { setD(null); dms.hrRequests(status).then(setD).catch(e => toast.error(errMsg(e))); }, [status, tick]);
  const decide = async (kind, id, action) => {
    const note = action === "reject" ? window.prompt("Reason for rejecting (optional)") : "";
    if (note === null) return;
    try { await dms.hrDecide(kind, id, action, note); toast.success(action === "approve" ? "Approved" : "Rejected"); setTick(t => t + 1); }
    catch (e) { toast.error(errMsg(e)); }
  };
  const Actions = ({ kind, r }) => r.status === "pending" ? (
    <div className="flex gap-1">
      <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700 h-8" onClick={() => decide(kind, r.id, "approve")}><Check size={14} /></Button>
      <Button size="sm" variant="outline" className="h-8 text-rose-600" onClick={() => decide(kind, r.id, "reject")}><Ban size={14} /></Button>
    </div>) : <Pill s={r.status} />;
  return (
    <div>
      <PageHeader title="Leave & Requests" subtitle="Approve leave and missed-punch requests. Approved items count in payroll." />
      <div className="flex gap-2 mb-3">
        {["pending", "approved", "rejected", "all"].map(s => (
          <button key={s} onClick={() => setStatus(s)} className={`px-3 py-1.5 rounded-full text-sm border capitalize ${status === s ? "bg-slate-900 text-white" : "bg-white"}`}>{s}</button>
        ))}
      </div>
      {!d ? <Card className="p-6 text-center text-slate-400">Loading…</Card> : <>
        <Card className="p-3 mb-4 overflow-x-auto">
          <div className="font-semibold mb-2">Leave ({d.leaves.length})</div>
          <Table>
            <TableHeader><TableRow><TableHead>Staff</TableHead><TableHead>Type</TableHead><TableHead>Dates</TableHead><TableHead>Days</TableHead><TableHead>Reason</TableHead><TableHead>Applied</TableHead><TableHead></TableHead></TableRow></TableHeader>
            <TableBody>
              {d.leaves.length === 0 && <TableRow><TableCell colSpan={7} className="text-center text-slate-400">Nothing here</TableCell></TableRow>}
              {d.leaves.map(l => (
                <TableRow key={l.id}>
                  <TableCell className="font-medium">{l.user_name}<div className="text-[11px] text-slate-500">{ROLE_LABEL[l.role]}</div></TableCell>
                  <TableCell>{d.types[l.type] || l.type}</TableCell>
                  <TableCell className="text-xs">{fmtD(l.from)}{l.to !== l.from ? ` – ${fmtD(l.to)}` : ""}</TableCell>
                  <TableCell>{l.half_day ? "½" : l.units}</TableCell>
                  <TableCell className="text-xs max-w-[220px]">{l.reason}</TableCell>
                  <TableCell className="text-xs">{fmtD(l.created_at)}</TableCell>
                  <TableCell><Actions kind="leave" r={l} /></TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Card>
        <Card className="p-3 overflow-x-auto">
          <div className="font-semibold mb-2">Missed Punch ({d.regularizations.length})</div>
          <Table>
            <TableHeader><TableRow><TableHead>Staff</TableHead><TableHead>Date</TableHead><TableHead>In – Out</TableHead><TableHead>Hours</TableHead><TableHead>Reason</TableHead><TableHead></TableHead></TableRow></TableHeader>
            <TableBody>
              {d.regularizations.length === 0 && <TableRow><TableCell colSpan={6} className="text-center text-slate-400">Nothing here</TableCell></TableRow>}
              {d.regularizations.map(r => (
                <TableRow key={r.id}>
                  <TableCell className="font-medium">{r.user_name}<div className="text-[11px] text-slate-500">{ROLE_LABEL[r.role]}</div></TableCell>
                  <TableCell className="text-xs">{fmtD(r.date)}</TableCell>
                  <TableCell className="text-xs">{r.in_time} – {r.out_time}</TableCell>
                  <TableCell>{r.hours}</TableCell>
                  <TableCell className="text-xs max-w-[220px]">{r.reason}</TableCell>
                  <TableCell><Actions kind="regularization" r={r} /></TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Card>
      </>}
    </div>
  );
}

// ---------------------------------------------------------------------------
// HR: Holidays & Leave Policy
// ---------------------------------------------------------------------------
export function HrHolidaysPage() {
  const [year, setYear] = useState(todayIst().slice(0, 4));
  const [rows, setRows] = useState([]);
  const [pol, setPol] = useState(null);
  const [nd, setNd] = useState({ date: "", name: "" });
  const [tick, setTick] = useState(0);
  useEffect(() => { dms.hrHolidays(year).then(r => setRows(r.data || [])).catch(e => toast.error(errMsg(e))); }, [year, tick]);
  useEffect(() => { dms.hrLeavePolicy().then(r => setPol(r.policy)).catch(() => {}); }, []);
  const [ptext, setPtext] = useState("");
  useEffect(() => { dms.hrPolicyText().then(r => setPtext(r.text || "")).catch(() => {}); }, []);
  const saveText = async () => {
    try { await dms.hrSavePolicyText(ptext); toast.success("Policy saved"); } catch (e) { toast.error(errMsg(e)); }
  };
  const downloadPolicy = async () => {
    try { await dms.hrSavePolicyText(ptext); await dms.policyPdf(year); } catch (e) { toast.error(errMsg(e)); }
  };
  const add = async () => {
    try { await dms.hrAddHoliday(nd); setNd({ date: "", name: "" }); toast.success("Holiday added"); setTick(t => t + 1); }
    catch (e) { toast.error(errMsg(e)); }
  };
  const del = async (id) => {
    if (!window.confirm("Delete this holiday?")) return;
    try { await dms.hrDeleteHoliday(id); setTick(t => t + 1); } catch (e) { toast.error(errMsg(e)); }
  };
  const savePol = async () => {
    try { await dms.hrSaveLeavePolicy(pol); toast.success("Leave policy saved"); } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div>
      <PageHeader title="Holidays & Leave Policy" subtitle="Holidays are paid days off. Leave balance is per calendar year." />
      <div className="grid md:grid-cols-2 gap-4">
        <Card className="p-4">
          <div className="flex items-center justify-between mb-3">
            <div className="font-semibold">Holidays</div>
            <Input type="number" value={year} onChange={e => setYear(e.target.value)} className="w-24 h-8" />
          </div>
          <div className="flex gap-2 mb-3">
            <Input type="date" value={nd.date} onChange={e => setNd({ ...nd, date: e.target.value })} className="w-40" />
            <Input placeholder="Holiday name" value={nd.name} onChange={e => setNd({ ...nd, name: e.target.value })} />
            <Button onClick={add}>Add</Button>
          </div>
          {rows.length === 0 ? <div className="text-sm text-slate-500">No holidays for {year}</div> : rows.map(h => (
            <div key={h.id} className="flex items-center justify-between py-1.5 border-b last:border-0 text-sm">
              <span><b>{fmtD(h.date)}</b> · {h.name}</span>
              <button onClick={() => del(h.id)} className="text-rose-600"><Trash2 size={15} /></button>
            </div>
          ))}
        </Card>
        <Card className="p-4">
          <div className="font-semibold mb-3">Leave per Year</div>
          {pol && <div className="space-y-2">
            {[["CL", "Casual Leave"], ["SL", "Sick Leave"], ["PL", "Paid Leave"]].map(([k, v]) => (
              <div key={k} className="flex items-center justify-between gap-3">
                <Label>{v}</Label>
                <Input type="number" min="0" value={pol[k]} onChange={e => setPol({ ...pol, [k]: e.target.value })} className="w-24" />
              </div>
            ))}
            <div className="text-xs text-slate-500">Leave Without Pay (LWP) has no limit and is not paid.</div>
            <Button onClick={savePol}>Save</Button>
          </div>}
        </Card>
      </div>
      <Card className="p-4 mt-4">
        <div className="flex items-center justify-between gap-2 flex-wrap mb-2">
          <div className="font-semibold">Policy document</div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={saveText}>Save text</Button>
            <Button onClick={downloadPolicy}>Download PDF ({year})</Button>
          </div>
        </div>
        <div className="text-xs text-slate-500 mb-2">The PDF has the leave table and the {year} holiday list from this page, then this text. Leave a blank line between paragraphs. Staff can download it from their Leave & Payslip page.</div>
        <Textarea rows={16} value={ptext} onChange={e => setPtext(e.target.value)} className="text-sm" />
      </Card>
    </div>
  );
}

// ---------------------------------------------------------------------------
// HR: Advances, bonus & deductions
// ---------------------------------------------------------------------------
export function HrAdvancesPage() {
  const staff = useStaff();
  const [month, setMonth] = useState(todayIst().slice(0, 7));
  const [adv, setAdv] = useState([]);
  const [adj, setAdj] = useState([]);
  const [tick, setTick] = useState(0);
  const [na, setNa] = useState({ user_id: "", amount: "", emi: "", start_month: todayIst().slice(0, 7), given_on: todayIst(), note: "" });
  const [nj, setNj] = useState({ user_id: "", kind: "bonus", amount: "", note: "" });
  useEffect(() => { dms.hrAdvances().then(r => setAdv(r.data || [])).catch(e => toast.error(errMsg(e))); }, [tick]);
  useEffect(() => { dms.hrAdjustments(month).then(r => setAdj(r.data || [])).catch(e => toast.error(errMsg(e))); }, [month, tick]);
  const addAdv = async () => {
    try { await dms.hrAddAdvance(na); toast.success("Advance added"); setNa({ ...na, user_id: "", amount: "", emi: "", note: "" }); setTick(t => t + 1); }
    catch (e) { toast.error(errMsg(e)); }
  };
  const closeAdv = async (id) => {
    const note = window.prompt("Stop further cuts for this advance? Note (optional)");
    if (note === null) return;
    try { await dms.hrCloseAdvance(id, note); setTick(t => t + 1); } catch (e) { toast.error(errMsg(e)); }
  };
  const addAdj = async () => {
    try { await dms.hrAddAdjustment({ ...nj, month }); toast.success("Saved"); setNj({ ...nj, user_id: "", amount: "", note: "" }); setTick(t => t + 1); }
    catch (e) { toast.error(errMsg(e)); }
  };
  const delAdj = async (id) => {
    try { await dms.hrDeleteAdjustment(id); setTick(t => t + 1); } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div>
      <PageHeader title="Advances, Bonus & Deductions" subtitle="Advance EMI is cut from salary every month until paid back." />
      <Card className="p-4 mb-4">
        <div className="font-semibold mb-3">Give Advance</div>
        <div className="grid md:grid-cols-6 gap-2 items-end">
          <div className="md:col-span-2"><Label className="text-xs">Staff</Label><StaffSelect staff={staff} value={na.user_id} onChange={v => setNa({ ...na, user_id: v })} /></div>
          <div><Label className="text-xs">Amount</Label><Input type="number" value={na.amount} onChange={e => setNa({ ...na, amount: e.target.value })} /></div>
          <div><Label className="text-xs">Cut per month</Label><Input type="number" value={na.emi} onChange={e => setNa({ ...na, emi: e.target.value })} /></div>
          <div><Label className="text-xs">First cut month</Label><Input type="month" value={na.start_month} onChange={e => setNa({ ...na, start_month: e.target.value })} /></div>
          <div><Label className="text-xs">Given on</Label><Input type="date" value={na.given_on} onChange={e => setNa({ ...na, given_on: e.target.value })} /></div>
          <div className="md:col-span-5"><Label className="text-xs">Note</Label><Input value={na.note} onChange={e => setNa({ ...na, note: e.target.value })} /></div>
          <Button onClick={addAdv}>Add</Button>
        </div>
        <div className="overflow-x-auto mt-4">
          <Table>
            <TableHeader><TableRow><TableHead>Staff</TableHead><TableHead>Amount</TableHead><TableHead>Per month</TableHead><TableHead>From</TableHead><TableHead>Recovered</TableHead><TableHead>Left</TableHead><TableHead>Status</TableHead><TableHead></TableHead></TableRow></TableHeader>
            <TableBody>
              {adv.length === 0 && <TableRow><TableCell colSpan={8} className="text-center text-slate-400">No advances</TableCell></TableRow>}
              {adv.map(a => (
                <TableRow key={a.id}>
                  <TableCell className="font-medium">{a.user_name}<div className="text-[11px] text-slate-500">{a.note}</div></TableCell>
                  <TableCell>{inr(a.amount)}</TableCell><TableCell>{inr(a.emi)}</TableCell><TableCell>{a.start_month}</TableCell>
                  <TableCell>{inr(a.recovered)}</TableCell><TableCell className="font-semibold">{inr(a.left)}</TableCell>
                  <TableCell><Pill s={a.left <= 0 && a.status === "active" ? "closed" : a.status} /></TableCell>
                  <TableCell>{a.status === "active" && a.left > 0 && <Button size="sm" variant="outline" onClick={() => closeAdv(a.id)}>Stop</Button>}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          <div className="text-xs text-slate-500 mt-2">"Recovered" counts finalized payroll months only.</div>
        </div>
      </Card>

      <Card className="p-4">
        <div className="flex items-center justify-between mb-3 gap-2 flex-wrap">
          <div className="font-semibold">Bonus & Deductions for</div>
          <Input type="month" value={month} onChange={e => e.target.value && setMonth(e.target.value)} className="w-44" />
        </div>
        <div className="grid md:grid-cols-6 gap-2 items-end">
          <div className="md:col-span-2"><Label className="text-xs">Staff</Label><StaffSelect staff={staff} value={nj.user_id} onChange={v => setNj({ ...nj, user_id: v })} /></div>
          <div><Label className="text-xs">Type</Label>
            <select value={nj.kind} onChange={e => setNj({ ...nj, kind: e.target.value })} className={`${selCls} w-full`}>
              <option value="bonus">Bonus (+)</option><option value="deduction">Deduction (−)</option></select></div>
          <div><Label className="text-xs">Amount</Label><Input type="number" value={nj.amount} onChange={e => setNj({ ...nj, amount: e.target.value })} /></div>
          <div><Label className="text-xs">Note</Label><Input value={nj.note} onChange={e => setNj({ ...nj, note: e.target.value })} /></div>
          <Button onClick={addAdj}>Add</Button>
        </div>
        <div className="mt-3">
          {adj.length === 0 ? <div className="text-sm text-slate-500">Nothing for {monthName(month)}</div> : adj.map(a => (
            <div key={a.id} className="flex items-center justify-between py-1.5 border-b last:border-0 text-sm">
              <span><b>{a.user_name}</b> · <span className={a.kind === "bonus" ? "text-emerald-700" : "text-rose-600"}>{a.kind === "bonus" ? "+" : "−"}{inr(a.amount)}</span> · {a.note}</span>
              <button onClick={() => delAdj(a.id)} className="text-rose-600"><Trash2 size={15} /></button>
            </div>
          ))}
        </div>
      </Card>
    </div>
  );
}

// ---------------------------------------------------------------------------
// HR: Performance Scorecard + incentive rules
// ---------------------------------------------------------------------------
function IncentivePanel({ onSaved }) {
  const [r, setR] = useState(null);
  const [metrics, setMetrics] = useState({});
  useEffect(() => { dms.hrIncentiveRules().then(d => { setR(d.rules); setMetrics(d.metrics); }).catch(() => {}); }, []);
  if (!r) return null;
  const slabs = r.slabs || [];
  const setSlab = (i, k, v) => setR({ ...r, slabs: slabs.map((s, j) => j === i ? { ...s, [k]: v } : s) });
  const save = async () => {
    try { const d = await dms.hrSaveIncentiveRules(r); setR(d.rules); toast.success("Incentive rules saved"); onSaved(); } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <Card className="p-4 mb-3">
      <div className="font-semibold mb-2">Sales incentive <span className="text-xs text-slate-500 font-normal">(salespersons, added to payroll)</span></div>
      <label className="flex items-center gap-2 text-sm mb-2"><input type="checkbox" checked={r.enabled} onChange={e => setR({ ...r, enabled: e.target.checked })} /> Incentive is on</label>
      <div className="flex flex-wrap gap-3 items-end mb-3">
        <div><Label className="text-xs">Based on</Label>
          <select value={r.metric} onChange={e => setR({ ...r, metric: e.target.value })} className={`${selCls} w-44`}>
            {Object.entries(metrics).map(([k, v]) => <option key={k} value={k}>{v} target %</option>)}
          </select></div>
        <div><Label className="text-xs">Plus ₹ per litre sold</Label><Input type="number" min="0" value={r.per_liter} onChange={e => setR({ ...r, per_liter: e.target.value })} className="w-32" /></div>
      </div>
      <div className="text-xs text-slate-500 mb-1">Slabs: the highest slab reached is paid.</div>
      {slabs.map((s, i) => (
        <div key={i} className="flex items-center gap-2 mb-2 text-sm">
          <span>At</span><Input type="number" value={s.min_pct} onChange={e => setSlab(i, "min_pct", e.target.value)} className="w-20" /><span>% or more → ₹</span>
          <Input type="number" value={s.amount} onChange={e => setSlab(i, "amount", e.target.value)} className="w-28" />
          <button onClick={() => setR({ ...r, slabs: slabs.filter((_, j) => j !== i) })} className="text-rose-600"><Trash2 size={15} /></button>
        </div>
      ))}
      <div className="flex gap-2">
        <Button variant="outline" onClick={() => setR({ ...r, slabs: [...slabs, { min_pct: "", amount: "" }] })}>Add slab</Button>
        <Button onClick={save}>Save incentive</Button>
      </div>
    </Card>
  );
}

const pctCell = (v) => v == null ? <span className="text-slate-400">-</span> :
  <span className={v >= 100 ? "text-emerald-700 font-semibold" : v >= 60 ? "text-amber-700" : "text-rose-600"}>{Math.round(v)}%</span>;

const pad2 = (n) => String(n).padStart(2, "0");
const ymdOf = (d) => `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;
function rangeOf(key) {
  const now = new Date(Date.now() + 5.5 * 3600 * 1000 + new Date().getTimezoneOffset() * 60000);
  const t = ymdOf(now);
  if (key === "today") return [t, t];
  if (key === "week") { const s = new Date(now); s.setDate(s.getDate() - ((s.getDay() + 6) % 7)); return [ymdOf(s), t]; }
  if (key === "last_month") return [ymdOf(new Date(now.getFullYear(), now.getMonth() - 1, 1)), ymdOf(new Date(now.getFullYear(), now.getMonth(), 0))];
  return [ymdOf(new Date(now.getFullYear(), now.getMonth(), 1)), t];
}
const PERIODS = [["today", "Today"], ["week", "This Week"], ["month", "This Month"], ["last_month", "Last Month"], ["custom", "Custom"]];

// Score basis editor: used for a role and for one person.
function BasisEditor({ metrics, barMetrics, value, onSave, onClear, saveLabel }) {
  const [w, setW] = useState(() => ({ ...(value?.weights || {}) }));
  const [bars, setBars] = useState(() => ({ ...(value?.bars || {}) }));
  useEffect(() => { setW({ ...(value?.weights || {}) }); setBars({ ...(value?.bars || {}) }); }, [value]);
  const total = Object.values(w).reduce((a, b) => a + Number(b || 0), 0);
  return (
    <div>
      <div className="grid md:grid-cols-2 gap-2">
        {Object.entries(metrics).map(([k, label]) => {
          const on = w[k] !== undefined;
          return (
            <div key={k} className={`rounded-lg border p-2 ${on ? "border-slate-400 bg-slate-50" : "border-slate-200"}`}>
              <label className="flex items-center gap-2 text-sm">
                <input type="checkbox" checked={on} onChange={e => setW(x => { const y = { ...x }; if (e.target.checked) y[k] = 10; else delete y[k]; return y; })} />
                <span className="flex-1">{label}</span>
                {on && <><Input type="number" min="1" value={w[k]} onChange={e => setW(x => ({ ...x, [k]: e.target.value }))} className="w-20 h-8" /><span className="text-xs text-slate-500">weight</span></>}
              </label>
              {on && barMetrics[k] && (
                <div className="flex items-center gap-2 mt-2 text-xs">
                  <span>{barMetrics[k]} =</span>
                  <Input type="number" min="0" value={bars[k] || ""} onChange={e => setBars(x => ({ ...x, [k]: e.target.value }))} className="w-32 h-8" />
                  <span>gives 100</span>
                </div>
              )}
            </div>
          );
        })}
      </div>
      <div className="text-xs text-slate-500 mt-2">Total weight {total}. Each measure gives 0–100; the score is the weighted average. A measure with no target or data is skipped.</div>
      <div className="flex gap-2 mt-3">
        <Button onClick={() => onSave({ weights: w, bars })}>{saveLabel || "Save"}</Button>
        {onClear && <Button variant="outline" onClick={onClear}>Use role basis</Button>}
      </div>
    </div>
  );
}

function BasisPanel({ cfg, onSaved }) {
  const [role, setRole] = useState("salesperson");
  const save = async (body) => {
    try { await dms.hrSaveScoreProfile(role, body); toast.success("Score basis saved"); onSaved(); } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <Card className="p-4 mb-3">
      <div className="font-semibold mb-1">Score basis — what performance is measured on</div>
      <div className="text-xs text-slate-500 mb-3">Set it for each role. For Team Leaders and Regional Managers, sales, litres, collection and visits are their whole team's total.</div>
      <div className="flex gap-2 mb-3">
        {Object.entries(cfg.roles).map(([k, v]) => (
          <button key={k} onClick={() => setRole(k)} className={`px-3 py-1.5 rounded-full text-sm border ${role === k ? "bg-slate-900 text-white" : "bg-white"}`}>{v}</button>
        ))}
      </div>
      <BasisEditor key={role} metrics={cfg.metrics} barMetrics={cfg.bar_metrics} value={cfg.profiles[role]} onSave={save} saveLabel={`Save for all ${cfg.roles[role]}s`} />
      {cfg.overrides.length > 0 && (
        <div className="mt-4 text-sm">
          <div className="font-semibold mb-1">People with their own basis</div>
          {cfg.overrides.map(o => <div key={o.user_id} className="text-xs text-slate-600">{o.name} ({ROLE_LABEL[o.role] || o.role}): {Object.keys(o.weights).map(k => cfg.metrics[k]).join(", ")}</div>)}
        </div>
      )}
    </Card>
  );
}

function PersonBasisDialog({ person, cfg, onClose, onSaved }) {
  const own = cfg.overrides.find(o => o.user_id === person.id);
  const save = async (body) => {
    try { await dms.hrSaveScoreOverride(person.id, body); toast.success("Saved"); onSaved(); onClose(); } catch (e) { toast.error(errMsg(e)); }
  };
  const clear = async () => {
    try { await dms.hrSaveScoreOverride(person.id, { clear: true }); toast.success("Now uses the role basis"); onSaved(); onClose(); } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <Modal title={`Score basis for ${person.name}`} onClose={onClose} wide>
      <div className="text-xs text-slate-500 mb-3">{own ? "This person has their own basis." : `Now using the ${ROLE_LABEL[person.role] || person.role} basis. Change it below to give this person their own.`}</div>
      <BasisEditor metrics={cfg.metrics} barMetrics={cfg.bar_metrics} value={own || cfg.profiles[person.role]} onSave={save} onClear={own ? clear : null} saveLabel="Save for this person" />
    </Modal>
  );
}

function ScoreTable({ rows, cfg, onBasis, team }) {
  const shown = Object.keys(cfg?.metrics || {});
  return (
    <Table>
      <TableHeader><TableRow>
        <TableHead>#</TableHead><TableHead>Name</TableHead><TableHead>Score</TableHead>
        <TableHead>Sales (₹)</TableHead><TableHead>Litres</TableHead><TableHead>Collection (₹)</TableHead><TableHead>Orders</TableHead>
        <TableHead>Measured on</TableHead><TableHead></TableHead>
      </TableRow></TableHeader>
      <TableBody>
        {rows.length === 0 && <TableRow><TableCell colSpan={9} className="text-center text-slate-400">No scores for this period</TableCell></TableRow>}
        {rows.map(r => (
          <TableRow key={r.id}>
            <TableCell className="font-bold">{r.rank}</TableCell>
            <TableCell className="font-medium">{r.name}{team && <div className="text-[11px] text-slate-500">Team of {r.team_size}</div>}
              <div className="text-[11px] text-slate-500">Attendance {r.present_days}/{r.working_days}{r.late_days ? ` · ${r.late_days} late` : ""}</div></TableCell>
            <TableCell><div className="flex items-center gap-1.5"><Grade g={r.grade} /><b>{r.score}</b></div>
              {r.basis === "custom" && <div className="text-[10px] text-indigo-600">own basis</div>}</TableCell>
            <TableCell className="whitespace-nowrap">{inr(r.sales.order_value)}{r.target && <div className="text-[11px]">{pctCell(r.pct.order_value)} of {inr(r.target.order_value)}</div>}</TableCell>
            <TableCell className="whitespace-nowrap">{Math.round(r.sales.order_liters)} L{r.target && <div className="text-[11px]">{pctCell(r.pct.order_liters)}</div>}</TableCell>
            <TableCell className="whitespace-nowrap">{inr(r.sales.collection)}{r.target && <div className="text-[11px]">{pctCell(r.pct.collection)}</div>}</TableCell>
            <TableCell>{r.sales.orders}</TableCell>
            <TableCell className="text-[11px] text-slate-600 max-w-[260px]">
              {shown.filter(k => r.parts?.[k] != null).map(k => <span key={k} className="inline-block mr-2">{cfg.metrics[k]}: <b>{Math.round(r.parts[k])}</b></span>)}
            </TableCell>
            <TableCell><Button size="sm" variant="outline" onClick={() => onBasis(r)}>Set basis</Button></TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
}

export function HrScorecardPage() {
  const [period, setPeriod] = useState("month");
  const [[from, to], setRange] = useState(rangeOf("month"));
  const [d, setD] = useState(null);
  const [cfg, setCfg] = useState(null);
  const [tick, setTick] = useState(0);
  const [panel, setPanel] = useState(null);
  const [tab, setTab] = useState("salesperson");
  const [q, setQ] = useState("");
  const [person, setPerson] = useState(null);
  useEffect(() => { setD(null); dms.hrScorecard({ date_from: from, date_to: to }).then(setD).catch(e => toast.error(errMsg(e))); }, [from, to, tick]);
  useEffect(() => { dms.hrScoreProfiles().then(setCfg).catch(() => {}); }, [tick]);
  const pick = (k) => { setPeriod(k); if (k !== "custom") setRange(rangeOf(k)); };
  const list = useMemo(() => {
    const src = !d ? [] : tab === "salesperson" ? d.ranked : tab === "team_leader" ? d.team_leaders : d.regional_managers;
    const term = q.trim().toLowerCase();
    return src.filter(r => !term || r.name.toLowerCase().includes(term));
  }, [d, tab, q]);
  const s = d?.summary;
  const reload = () => setTick(t => t + 1);
  return (
    <div>
      <PageHeader title="Performance Scorecard" subtitle="Who sold how much, and a score out of 100 on the basis you choose." />
      <Card className="p-3 mb-3">
        <div className="flex flex-wrap gap-2 mb-2">
          {PERIODS.map(([k, v]) => (
            <button key={k} onClick={() => pick(k)} className={`px-3 py-1.5 rounded-full text-sm border ${period === k ? "bg-slate-900 text-white" : "bg-white"}`}>{v}</button>
          ))}
        </div>
        <div className="flex flex-wrap gap-2 items-center">
          {period === "custom" && <>
            <Input type="date" value={from} max={to} onChange={e => setRange([e.target.value, to])} className="w-40" />
            <Input type="date" value={to} min={from} onChange={e => setRange([from, e.target.value])} className="w-40" />
          </>}
          <Input placeholder="Search name…" value={q} onChange={e => setQ(e.target.value)} className="w-52" />
          <Button variant="outline" onClick={() => setPanel(panel === "b" ? null : "b")}>Score basis</Button>
          <Button variant="outline" onClick={() => setPanel(panel === "i" ? null : "i")}>Incentive rules</Button>
        </div>
      </Card>
      {panel === "b" && cfg && <BasisPanel cfg={cfg} onSaved={reload} />}
      {panel === "i" && <IncentivePanel onSaved={reload} />}
      {!d ? <Card className="p-6 text-center text-slate-400">Loading…</Card> : <>
        <div className="text-xs text-slate-500 mb-2">{fmtD(d.from)} – {fmtD(d.to)} ({d.days} day{d.days === 1 ? "" : "s"}). Targets are for these days only.</div>
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-8 gap-3 mb-3">
          <Card className="p-3"><div className="text-[11px] uppercase text-slate-500">Total sales</div><div className="text-lg font-bold">{inr(s.sales.order_value)}</div></Card>
          <Card className="p-3"><div className="text-[11px] uppercase text-slate-500">Litres</div><div className="text-lg font-bold">{Math.round(s.sales.order_liters)} L</div></Card>
          <Card className="p-3"><div className="text-[11px] uppercase text-slate-500">Collection</div><div className="text-lg font-bold">{inr(s.sales.collection)}</div></Card>
          <Card className="p-3"><div className="text-[11px] uppercase text-slate-500">Avg score</div><div className="text-lg font-bold">{s.avg_score ?? "-"}</div></Card>
          {"ABCD".split("").map(g => (
            <Card key={g} className="p-3"><div className="text-[11px] uppercase text-slate-500 flex items-center gap-1">Grade <Grade g={g} /></div><div className="text-lg font-bold">{s.grades[g]}</div></Card>
          ))}
        </div>
        <div className="flex gap-2 mb-2">
          {[["salesperson", "Salespersons"], ["team_leader", "Team Leaders"], ["regional_manager", "Regional Managers"]].map(([k, v]) => (
            <button key={k} onClick={() => setTab(k)} className={`px-3 py-1.5 rounded-full text-sm border ${tab === k ? "bg-slate-900 text-white" : "bg-white"}`}>{v}</button>
          ))}
        </div>
        <Card className="p-3 overflow-x-auto">
          <ScoreTable rows={list} cfg={cfg || { metrics: d.metric_labels }} team={tab !== "salesperson"} onBasis={(r) => cfg && setPerson(r)} />
          {d.unscored.length > 0 && <div className="text-xs text-slate-500 mt-2">Not scored (no data for this period): {d.unscored.map(u => u.name).join(", ")}</div>}
        </Card>
      </>}
      {person && cfg && <PersonBasisDialog person={person} cfg={cfg} onClose={() => setPerson(null)} onSaved={reload} />}
    </div>
  );
}

// ---------------------------------------------------------------------------
// HR: Offer letters
// ---------------------------------------------------------------------------
const fillLetter = (body, f, company) => Object.entries({ company: company?.name || "", ...f })
  .reduce((t, [k, v]) => t.split(`{${k}}`).join(v || "____"), body || "");

export function HrLettersPage() {
  const [d, setD] = useState(null);
  const [tick, setTick] = useState(0);
  const [form, setForm] = useState(null);
  useEffect(() => { dms.hrLetters().then(setD).catch(e => toast.error(errMsg(e))); }, [tick]);
  const blank = () => ({ fields: { letter_date: fmtD(todayIst()), probation: "3" }, body: d.default_body, company: { ...d.company } });
  const del = async (id) => {
    if (!window.confirm("Delete this letter?")) return;
    try { await dms.hrDeleteLetter(id); setTick(t => t + 1); } catch (e) { toast.error(errMsg(e)); }
  };
  const pdf = (l) => dms.hrLetterPdf(l.id, `Offer_Letter_${(l.fields.name || "letter").replace(/\s+/g, "_")}.pdf`).catch(e => toast.error(errMsg(e)));
  if (!d) return <div><PageHeader title="Offer Letters" /><div className="text-sm text-slate-500">Loading…</div></div>;
  if (form) return <LetterForm init={form} meta={d} onCancel={() => setForm(null)} onSaved={(id) => { setForm(null); setTick(t => t + 1); if (id) dms.hrLetterPdf(id, "Offer_Letter.pdf").catch(() => {}); }} />;
  return (
    <div>
      <PageHeader title="Offer Letters" subtitle="Fill the details, check the text and download a PDF." action={<Button onClick={() => setForm(blank())}>New offer letter</Button>} />
      <Card className="p-3 overflow-x-auto">
        <Table>
          <TableHeader><TableRow><TableHead>Name</TableHead><TableHead>Position</TableHead><TableHead>Joining</TableHead><TableHead>Salary</TableHead><TableHead>Made on</TableHead><TableHead></TableHead></TableRow></TableHeader>
          <TableBody>
            {d.data.length === 0 && <TableRow><TableCell colSpan={6} className="text-center text-slate-400">No letters yet</TableCell></TableRow>}
            {d.data.map(l => (
              <TableRow key={l.id}>
                <TableCell className="font-medium">{l.fields.name}</TableCell><TableCell>{l.fields.position}</TableCell>
                <TableCell className="text-xs">{l.fields.joining_date}</TableCell><TableCell>₹{l.fields.salary}</TableCell>
                <TableCell className="text-xs">{fmtD(l.at)}</TableCell>
                <TableCell className="whitespace-nowrap">
                  <Button size="sm" onClick={() => pdf(l)} className="mr-1">PDF</Button>
                  <Button size="sm" variant="outline" className="mr-1" onClick={() => setForm({ ...l })}>Edit</Button>
                  <Button size="sm" variant="outline" className="mr-1" onClick={() => setForm({ fields: { ...l.fields, name: "" }, body: l.body, company: l.company })}>Copy</Button>
                  <button onClick={() => del(l.id)} className="text-rose-600 align-middle"><Trash2 size={15} /></button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Card>
    </div>
  );
}

function LetterForm({ init, meta, onCancel, onSaved }) {
  const [f, setF] = useState(init.fields || {});
  const [body, setBody] = useState(init.body || meta.default_body);
  const [company, setCompany] = useState(init.company || meta.company);
  const [busy, setBusy] = useState(false);
  const save = async () => {
    setBusy(true);
    try { const r = await dms.hrSaveLetter({ id: init.id, fields: f, body, company }); toast.success("Letter saved, PDF downloading"); onSaved(r.id); }
    catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  const types = { joining_date: "text", salary: "number", probation: "number" };
  return (
    <div>
      <PageHeader title={init.id ? "Edit offer letter" : "New offer letter"} subtitle="Words in {curly brackets} in the text are filled from the form." />
      <div className="grid lg:grid-cols-2 gap-4">
        <Card className="p-4 space-y-3">
          <div className="font-semibold">Company</div>
          <div className="grid grid-cols-2 gap-2">
            <div><Label className="text-xs">Company name</Label><Input value={company.name || ""} onChange={e => setCompany({ ...company, name: e.target.value })} /></div>
            <div><Label className="text-xs">Phone</Label><Input value={company.phone || ""} onChange={e => setCompany({ ...company, phone: e.target.value })} /></div>
            <div className="col-span-2"><Label className="text-xs">Address</Label><Input value={company.address || ""} onChange={e => setCompany({ ...company, address: e.target.value })} /></div>
          </div>
          <div className="font-semibold pt-2">Candidate & offer</div>
          <div className="grid grid-cols-2 gap-2">
            {Object.entries(meta.fields).map(([k, label]) => (
              <div key={k} className={k === "address" ? "col-span-2" : ""}>
                <Label className="text-xs">{label}{["name", "position", "joining_date", "salary"].includes(k) ? " *" : ""}</Label>
                <Input type={types[k] || "text"} value={f[k] || ""} onChange={e => setF({ ...f, [k]: e.target.value })} />
              </div>
            ))}
          </div>
          <div className="font-semibold pt-2">Letter text</div>
          <Textarea rows={14} value={body} onChange={e => setBody(e.target.value)} className="font-mono text-xs" />
          <div className="flex gap-2">
            <Button onClick={save} disabled={busy}>{busy ? "Saving…" : "Save and download PDF"}</Button>
            <Button variant="outline" onClick={() => setBody(meta.default_body)}>Reset text</Button>
            <Button variant="outline" onClick={onCancel}>Cancel</Button>
          </div>
        </Card>
        <Card className="p-6 text-sm leading-relaxed bg-white">
          <div className="text-xs uppercase text-slate-400 mb-2">Preview</div>
          <div className="font-bold text-lg">{company.name}</div>
          <div className="text-xs text-slate-500">{company.address}{company.phone ? ` · Phone: ${company.phone}` : ""}</div>
          <hr className="my-3 border-slate-800" />
          <div>Date: {f.letter_date}</div>
          <div className="mt-2">To,<br />{f.name}{f.address ? <><br />{f.address}</> : null}</div>
          <div className="font-bold mt-3">Subject: Offer of employment - {f.position}</div>
          <div className="whitespace-pre-wrap mt-2">{fillLetter(body, f, company)}</div>
          <div className="mt-6">For {company.name}<br /><br /><br />{f.signatory_name}<br />{f.signatory_designation}</div>
        </Card>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// HR: Notices + the notice popup every user sees
// ---------------------------------------------------------------------------
const STAFF_ROLES = ["salesperson", "team_leader", "regional_manager", "hr", "owner_accountant"];

export function HrNoticesPage() {
  const [d, setD] = useState(null);
  const [tick, setTick] = useState(0);
  const [f, setF] = useState({ title: "", body: "", audience_roles: STAFF_ROLES, expires_on: "" });
  const [busy, setBusy] = useState(false);
  useEffect(() => { dms.hrNotices().then(setD).catch(e => toast.error(errMsg(e))); }, [tick]);
  const toggle = (r) => setF(x => ({ ...x, audience_roles: x.audience_roles.includes(r) ? x.audience_roles.filter(y => y !== r) : [...x.audience_roles, r] }));
  const send = async () => {
    setBusy(true);
    try {
      await dms.hrCreateNotice({ ...f, expires_on: f.expires_on || null });
      toast.success("Notice sent");
      setF({ title: "", body: "", audience_roles: STAFF_ROLES, expires_on: "" });
      setTick(t => t + 1);
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  const withdraw = async (id) => {
    if (!window.confirm("Withdraw this notice? People will stop seeing it.")) return;
    try { await dms.hrWithdrawNotice(id); setTick(t => t + 1); } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div>
      <PageHeader title="Notices" subtitle="A notice pops up for everyone you choose the next time they open the app. They close it with Cancel." />
      <Card className="p-4 mb-4 space-y-3">
        <div><Label className="text-xs">Title *</Label><Input value={f.title} onChange={e => setF({ ...f, title: e.target.value })} /></div>
        <div><Label className="text-xs">Notice *</Label><Textarea rows={5} value={f.body} onChange={e => setF({ ...f, body: e.target.value })} /></div>
        <div>
          <Label className="text-xs">Send to *</Label>
          <div className="flex flex-wrap gap-3 mt-1">
            {d && Object.entries(d.roles).map(([k, v]) => (
              <label key={k} className="flex items-center gap-1.5 text-sm"><input type="checkbox" checked={f.audience_roles.includes(k)} onChange={() => toggle(k)} />{v}</label>
            ))}
          </div>
        </div>
        <div className="flex items-end gap-3 flex-wrap">
          <div><Label className="text-xs">Show until (optional)</Label><Input type="date" value={f.expires_on} min={todayIst()} onChange={e => setF({ ...f, expires_on: e.target.value })} className="w-44" /></div>
          <Button onClick={send} disabled={busy}>{busy ? "Sending…" : "Send notice"}</Button>
        </div>
      </Card>
      <Card className="p-3">
        <div className="font-semibold mb-2">Sent notices</div>
        {!d ? <div className="text-sm text-slate-400">Loading…</div> : d.data.length === 0 ? <div className="text-sm text-slate-400">No notices yet</div> : d.data.map(n => (
          <div key={n.id} className="py-2 border-b last:border-0">
            <div className="flex items-start justify-between gap-2">
              <div>
                <div className="font-medium">{n.title} <Pill s={n.status === "active" ? "active" : "closed"} /></div>
                <div className="text-sm text-slate-600 whitespace-pre-wrap">{n.body}</div>
                <div className="text-[11px] text-slate-500 mt-1">
                  {fmtD(n.at)} by {n.created_by_name} · To: {(n.audience_roles || []).map(r => d.roles[r] || r).join(", ")}
                  {n.expires_on ? ` · until ${fmtD(n.expires_on)}` : ""} · Closed by {n.closed_count} of {n.audience_count}
                </div>
              </div>
              {n.status === "active" && <Button size="sm" variant="outline" onClick={() => withdraw(n.id)}>Withdraw</Button>}
            </div>
          </div>
        ))}
      </Card>
    </div>
  );
}

export function NoticeGate() {
  const { user, impersonation } = useAuth();
  const uid = user && !impersonation ? user.id : null;
  const [list, setList] = useState([]);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (!uid) { setList([]); return undefined; }
    let alive = true;
    const load = () => dms.myNotices().then(r => { if (alive) setList(r.data || []); }).catch(() => {});
    load();
    const t = setInterval(load, 5 * 60 * 1000);
    return () => { alive = false; clearInterval(t); };
  }, [uid]);
  const n = list[0];
  if (!uid || !n) return null;
  const close = async () => {
    setBusy(true);
    try { await dms.dismissNotice(n.id); } catch (e) { /* show the next one anyway */ }
    setList(l => l.slice(1));
    setBusy(false);
  };
  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4" style={{ zIndex: 2147482000 }} data-testid="notice-gate">
      <div className="bg-white dark:bg-slate-900 rounded-xl w-full max-w-md p-5 shadow-xl">
        <div className="text-[11px] uppercase tracking-wider text-amber-700 font-semibold mb-1">Notice{list.length > 1 ? ` (1 of ${list.length})` : ""}</div>
        <div className="text-lg font-bold mb-2">{n.title}</div>
        <div className="text-sm text-slate-700 whitespace-pre-wrap max-h-[50vh] overflow-y-auto">{n.body}</div>
        <div className="text-[11px] text-slate-500 mt-3">{n.created_by_name ? `From ${n.created_by_name} · ` : ""}{fmtD(n.at)}</div>
        <Button onClick={close} disabled={busy} className="w-full mt-4" data-testid="notice-cancel">Cancel</Button>
      </div>
    </div>
  );
}
